<script setup lang="ts">
const file = defineModel<File | null>()

const notification = useNotification()
const { t } = useI18n()

const filename = ref<string | null>(null)
const parsed = ref<boolean>(false)

const allowedExtensionsFile = readonly(ref(['.csv', '.xlsx', '.ods']))

const parseFile = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (!target.files || target.files.length === 0) {
    return
  }

  filename.value = target.files[0].name
  const extension = `.${filename.value.split('.').pop().toLowerCase()}`

  if (!allowedExtensionsFile.value.includes(extension)) {
    filename.value = null
    parsed.value = false
    notification.create('error', {
      text: t('pages.import.importPage.notifications.unsupportedFileType'),
      timeout: 5000
    })
    return
  }

  parsed.value = true
  file.value = target.files[0]
}
</script>
<template>
  <div>
    <h2 class="text-lg font-medium">{{ t('pages.import.importPage.initialize.title') }}</h2>
    <div class="mb-6 text-sm text-muted-foreground">
      {{ t('pages.import.importPage.initialize.subtitle') }}
    </div>

    <BaseTip severity="info" class="mb-6">
      <template #text>
        <span class="font-semibold">{{ t('pages.import.importPage.initialize.csvRecommendedTitle') }}</span>
        {{ ' ' }}
        <span>{{ t('pages.import.importPage.initialize.csvRecommendedDescription') }}</span>
      </template>
    </BaseTip>

    <div class="rounded-xl border bg-white p-4">
      <div
        class="relative mb-3 rounded-lg border-2 border-dashed p-8"
        :class="filename ? 'bg-green-50' : 'border-border'"
      >
        <input
          id="file"
          type="file"
          name="file"
          :accept="allowedExtensionsFile.join(',')"
          class="relative z-10 block h-full w-full cursor-pointer p-20 opacity-0"
          @change="parseFile"
        />
        <div class="absolute bottom-0 left-0 right-0 top-0 flex flex-col items-center justify-center">
          <BaseIcon
            v-if="!filename"
            name="HeroCloudArrowUpIconOutline"
            class="mx-auto mb-4 h-12 w-12 text-foreground-disabled"
          />
          <CloudArrowUpIconSolid v-else class="mx-auto mb-4 h-12 w-12 text-green-400" />
          <h4>
            {{ parsed ? filename : t('pages.import.importPage.initialize.drag') }}
          </h4>
          <p
            class="font-medium text-primary focus-within:outline-hidden focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:text-primary/80"
          >
            {{
              parsed ? t('pages.import.importPage.initialize.replace') : t('pages.import.importPage.initialize.select')
            }}
          </p>
        </div>
      </div>
      <p class="text-sm text-muted-foreground">
        {{ t('pages.import.importPage.initialize.supportedFiles') }}
      </p>
    </div>

    <Alert class="mt-6">
      <AlertDescription>
        <ul class="flex list-disc flex-col gap-1 pl-4">
          <li>
            <span class="font-semibold text-foreground"
              >{{ t('pages.import.importPage.initialize.noDuplicates') }} :
            </span>
            <span>{{ t('pages.import.importPage.initialize.noDuplicatesDescription') }}</span>
          </li>
          <li>
            <span class="font-semibold text-foreground"
              >{{ t('pages.import.importPage.initialize.pointsCumulated') }} :
            </span>
            <span>{{ t('pages.import.importPage.initialize.pointsCumulatedDescription') }}</span>
          </li>
          <li>
            <span class="font-semibold text-foreground"
              >{{ t('pages.import.importPage.initialize.automatedScenarios') }} :
            </span>
            <span>{{ t('pages.import.importPage.initialize.automatedScenariosDescription') }}</span>
          </li>
          <li>
            <span class="font-semibold text-foreground"
              >{{ t('pages.import.importPage.initialize.subscriptionDate') }} :
            </span>
            <span>{{ t('pages.import.importPage.initialize.subscriptionDateDescription') }}</span>
          </li>
        </ul>
      </AlertDescription>
    </Alert>
  </div>
</template>
